<template>
	<!-- One picker, under the drop zone.
	--
	-- It was two: a row of coloured dots for the blank cards and a grid of
	-- thumbnails for the pictures. The dots were the problem -- four small
	-- circles of colour, directly above a row of four small circles of colour
	-- that set the LETTERING colour, and the reasonable question about them was
	-- "why the colours there?". A blank card is a thing you start from, not a
	-- colour you choose, so it is a card now: same size, same border, same
	-- hover as the pictures beside it.
	--
	-- Modelled on the six sample photographs the home page offers under "or try
	-- one of these", because that is the picker visitors have already met. The
	-- difference is where the thumbnails come from: the samples go through
	-- ImageEngine with ?imgeng=/w_240, which resizes them for nothing, and
	-- ImageEngine does NOT front this site's own static assets -- measured --
	-- so these are resized at build time by tools/make_library.py instead. -->
	<div class="flex flex-col gap-2">
		<div class="text-xs font-bold uppercase tracking-[0.09em] text-ink-label">
			{{ t('meme.library') }}
		</div>
		<div class="grid grid-cols-4 gap-2">
			<button v-for="picture in LIBRARY" :key="picture.key" type="button"
				:data-library="picture.key"
				:title="picture.name"
				:disabled="busy === picture.key"
				@click.prevent="choosePicture(picture.key)"
				class="relative block overflow-hidden rounded-[10px] border border-rule transition hover:border-brand disabled:opacity-50">
				<!-- width and height on the element, so twelve of these cannot
				-- reflow the controls under them as they arrive. -->
				<img :src="thumbFor(picture.key)" :srcset="thumbFor2x(picture.key) + ' 2x'"
					:alt="picture.alt" loading="lazy" decoding="async"
					width="240" height="161"
					class="block aspect-[4/3] w-full object-cover" />
				<!-- The name, on the picture. It is the only place a visitor --
				-- or a search engine -- is told in words what these are of. -->
				<span class="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-1 py-0.5 text-[10px] font-bold leading-tight text-white">{{ picture.name }}</span>
			</button>

			<!-- The blanks, last, because a picture is what most people came
			-- for. Drawn in CSS rather than fetched: they are a flat colour or
			-- a gradient, and lib/starters.js draws the real one on click. -->
			<button v-for="blank in MEME_STARTERS" :key="blank.key" type="button"
				:data-starter="blank.key"
				:title="t('meme.starter.' + blank.key)"
				:aria-label="t('meme.starter.' + blank.key)"
				:disabled="busy === blank.key"
				@click.prevent="chooseBlank(blank)"
				class="relative block overflow-hidden rounded-[10px] border border-rule transition hover:border-brand disabled:opacity-50">
				<span class="block aspect-[4/3] w-full" :style="{ background: blank.swatch }"></span>
				<span class="pointer-events-none absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/70 to-transparent px-1 py-0.5 text-[10px] font-bold leading-tight text-white">{{ t('meme.starter.' + blank.key) }}</span>
			</button>
		</div>
	</div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { LIBRARY, thumbFor, thumbFor2x, libraryFile, areasFor } from "../lib/library"
import { MEME_STARTERS, starterToFile } from "../lib/starters"

// `areas` travels with the file: a picture that has its own white caption
// spaces has to arrive with the rectangles already over them, or the first
// thing the visitor sees is words in the wrong place on artwork that plainly
// says where they go.
const emit = defineEmits(['picture'])
const { t } = useI18n()

// Which one is being fetched. A 150 KB picture over a slow connection is long
// enough to press twice, and two of the same picture arriving is a batch.
const busy = ref("")

async function choosePicture(key) {
	if (busy.value) return
	busy.value = key
	try {
		const file = await libraryFile(key)
		if (file) emit('picture', {file, areas: areasFor(key)})
	} finally {
		busy.value = ""
	}
}

async function chooseBlank(blank) {
	if (busy.value) return
	busy.value = blank.key
	try {
		const file = await starterToFile(blank)
		// A blank has no caption spaces of its own; the default bands are right.
		if (file) emit('picture', {file, areas: []})
	} finally {
		busy.value = ""
	}
}
</script>
